import { App } from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/JwtService";
import { AxiosResponse, AxiosRequestConfig } from "axios";
import router from "@/router/clean";
import store from "@/store";
import { Actions, Mutations } from "@/store/enums/StoreEnums";

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description property to share vue instance
   */
  public static vueInstance: App;

  /**
   * @description initialize vue axios
   */
  public static init(app: App<Element>) {
    ApiService.vueInstance = app;
    ApiService.vueInstance.use(VueAxios, axios);
    ApiService.vueInstance.axios.defaults.baseURL = process.env.VUE_APP_API_URL;
    // Admin "View as customer" tab: portal requests carry the view-as session (this tab only -
    // it's kept in sessionStorage, so the admin's other tabs are unaffected)
    ApiService.vueInstance.axios.interceptors.request.use((config) => {
      try {
        const raw = window.sessionStorage.getItem("bender-view-as");
        if (raw && /api\/v1\/portal\//.test(config.url ?? "")) {
          const session = JSON.parse(raw);
          config.params = { ...(config.params || {}), viewAs: session.id };
        }
      } catch {
        // no session storage: not a view-as tab
      }
      return config;
    });
    ApiService.vueInstance.axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        // Sign-in endpoints answer 401/403 for a wrong password or MFA code: the page shows the
        // message itself, so don't treat those as an expired session.
        const url: string = error.config?.url ?? "";
        const isSignIn = /api\/v1\/auth\/(login|mfa-verify|mfa-setup)/.test(
          url
        );
        // The customer portal answers 403 with a message for the page to show (e.g. an account
        // that isn't set up yet), so only a 401 there means the session has expired.
        const isPortal403 =
          /api\/v1\/portal\//.test(url) && error.response?.status === 403;
        if (
          !isSignIn &&
          !isPortal403 &&
          error.response &&
          (error.response.status === 403 || error.response.status === 401)
        ) {
          store.commit(Mutations.PURGE_AUTH);
          router.push({ name: "sign-in" });
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * @description set the default HTTP request headers
   */
  public static setHeader(): void {
    // clear console
    ApiService.vueInstance.axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${JwtService.getToken()}`;
    ApiService.vueInstance.axios.defaults.headers.common["Accept"] =
      "application/json";
  }

  public static setEmptyHeader(): void {
    ApiService.vueInstance.axios.defaults.headers.common["Authorization"] =
      null;
    ApiService.vueInstance.axios.defaults.headers.common["Accept"] =
      "application/json";
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static query(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(resource, params);
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param slug: string
   * @returns Promise<AxiosResponse>
   */
  public static get(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(`${resource}`);
  }

  /**
   * @description set the POST HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static post(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.post(`${resource}`, params);
  }

  public static upload(
    resource: string,
    file: FormData
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.post(`${resource}`, file, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  /**
   * @description send the UPDATE HTTP request
   * @param resource: string
   * @param slug: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static update(
    resource: string,
    slug: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params);
  }

  /**
   * @description Send the PUT HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static put(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}`, params);
  }

  /**
   * @description Send the DELETE HTTP request
   * @param resource: string
   * @returns Promise<AxiosResponse>
   */
  public static delete(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.delete(resource);
  }
}

export default ApiService;
